import {DialogTitle} from '@apps-common/ui-components';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  ToggleButton,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import {useTranslation} from 'react-i18next';

import {ToggleButtonGroup} from './snapshot.styles.jsx';
import {
  ASPECT_RATIOS,
  EXPORT_FILE_TYPE,
  EXPORT_SIZES,
  getHeightFromWidth,
} from '@explorer/util/snapshot';

const DownloadDialog = ({
  open,
  handleToggle,
  aspectRatio,
  selectSize,
  size,
  filename,
  setFilename,
  handleDownload,
}) => {
  const {t} = useTranslation();

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleToggle}
      aria-labelledby="snapshot-download-dialog-title"
      aria-describedby="snapshot-download-dialog-description"
    >
      <DialogTitle
        id="snapshot-download-dialog-title"
        title={t('snapshotDownloadDialog.title', 'Download Snapshot')}
        onClose={handleToggle}
      />
      <DialogContent id="snapshot-download-dialog-description">
        <Typography
          variant="caption"
          align="left"
          gutterBottom
          color="textSecondary"
          sx={{
            display: 'block',
          }}
        >
          {t('snapshotDownloadDialog.imageSize', 'Image Size')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 1,
          }}
        >
          <ToggleButtonGroup
            value={size}
            onChange={selectSize}
            aria-label="Snapshot Size"
            sx={{
              width: '100%',
              display: 'flex',
              '& button': {
                flexGrow: 1,
              },
            }}
          >
            <ToggleButton
              selected={size === 'small'}
              value="small"
              aria-label="small"
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <ImageIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2">
                  {t('snapshotDownloadDialog.sizeSmall')}
                </Typography>
                <Typography variant="caption">
                  {`(${EXPORT_SIZES.small} x ${getHeightFromWidth(
                    'small',
                    aspectRatio
                  )} px)`}
                </Typography>
              </Box>
            </ToggleButton>

            <ToggleButton
              selected={size === 'medium'}
              value="medium"
              aria-label="medium"
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <ImageIcon sx={{fontSize: 30}} color="primary" />
                <Typography variant="subtitle2">
                  {t('snapshotDownloadDialog.sizeMedium')}
                </Typography>
                <Typography variant="caption">
                  {t('snapshotDownloadDialog.tooltipSize', {
                    width: EXPORT_SIZES.medium,
                    height: getHeightFromWidth('medium', aspectRatio),
                  })}
                </Typography>
              </Box>
            </ToggleButton>

            <ToggleButton
              selected={size === 'large'}
              value="large"
              aria-label="large"
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <ImageIcon sx={{fontSize: 40}} color="primary" />
                <Typography variant="subtitle2">
                  {t('snapshotDownloadDialog.sizeLarge')}
                </Typography>
                <Typography variant="caption">
                  {t('snapshotDownloadDialog.tooltipSize', {
                    width: EXPORT_SIZES.large,
                    height: getHeightFromWidth('large', aspectRatio),
                  })}
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box
          sx={{
            mb: 1,
          }}
        >
          <TextField
            autoFocus
            variant="outlined"
            margin="dense"
            fullWidth
            value={filename}
            placeholder="planet-snapshot"
            onChange={setFilename}
            label={t('snapshotDownloadDialog.labelFileName')}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="start">
                    {`.${EXPORT_FILE_TYPE}`}
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDownload} color="primary" variant="contained">
          {t('snapshotDownloadDialog.downloadButton', 'Download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DownloadDialog.propTypes = {
  open: PropTypes.bool,
  handleToggle: PropTypes.func.isRequired,
  selectSize: PropTypes.func.isRequired,
  setFilename: PropTypes.func.isRequired,
  handleDownload: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']).isRequired,
  aspectRatio: PropTypes.oneOf(Object.values(ASPECT_RATIOS)).isRequired,
  filename: PropTypes.string,
};

export default DownloadDialog;
